import { useMemo, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { Plus, Settings } from "lucide-react";
import ListTaskRow from "@/components/tasks/lists/ListTaskRow/ListTaskRow";
import ListTemplateRow from "@/components/tasks/lists/ListTemplateRow/ListTemplateRow";
import AppShell from "@/components/foundations/core/AppShell/AppShell";
import CreateTaskSheet from "@/components/tasks/sheets/CreateTaskSheet/CreateTaskSheet";
import PatternAddSection, {
  createPatternAddSection,
  patternSectionToSelection,
  PatternAddSectionValue,
} from "@/components/tasks/patterns/PatternAddSection/PatternAddSection";
import TaskSwipeActions from "@/components/tasks/lists/TaskSwipeActions/TaskSwipeActions";
import TaskStatusDivider, {
  shouldShowStatusDivider,
  statusCount,
  sortTasksByStatus,
  useCollapsedStatuses,
} from "@/components/tasks/lists/TaskStatusDivider/TaskStatusDivider";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DEFAULT_AREA_COLOR, getListColor } from "@/lib/listColors";
import { listSlug } from "@/lib/listSlug";
import { nowTimeString, todayKey } from "@/lib/time";
import { deriveTaskLifecycle } from "@shared/taskLifecycle";
import { trpc } from "@/lib/trpc";

export default function ListDetail() {
  const [, params] = useRoute<{ category?: string; slug: string }>("/lists/:category/:slug");
  const [, shortParams] = useRoute<{ slug: string }>("/lists/:slug");
  const [, setLocation] = useLocation();
  const slug = params?.slug ?? shortParams?.slug ?? "";
  const categorySlug = params?.category ?? null;
  const utils = trpc.useUtils();
  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const [patternSheet, setPatternSheet] = useState<PatternAddSectionValue | null>(null);
  const statusGroups = useCollapsedStatuses();
  const listsQuery = trpc.lists.list.useQuery();
  const categoriesQuery = trpc.categories.list.useQuery();
  const list = useMemo(() => {
    const categoriesById = new Map((categoriesQuery.data ?? []).map(category => [category.id, category]));
    return listsQuery.data?.find(l =>
      slug === "inbox"
        ? l.kind === "inbox"
        : listSlug(l.name) === slug &&
          (!categorySlug || listSlug(categoriesById.get(l.categoryId ?? 0)?.name ?? "") === categorySlug),
    );
  }, [listsQuery.data, categoriesQuery.data, categorySlug, slug]);

  const tasksQuery = trpc.tasks.listByList.useQuery(
    { listId: list?.id ?? 0, today: todayKey() },
    { enabled: !!list },
  );
  const templatesQuery = trpc.templates.list.useQuery(
    { listId: list?.id ?? 0 },
    { enabled: !!list },
  );

  const colors = getListColor(list?.color ?? DEFAULT_AREA_COLOR);
  const tasks = sortTasksByStatus(tasksQuery.data ?? []);
  const templates = templatesQuery.data ?? [];
  const markDone = trpc.tasks.markDone.useMutation({
    onSuccess: () => {
      utils.tasks.listByList.invalidate();
      utils.lists.overview.invalidate();
      utils.day.monthSummary.invalidate();
    },
  });
  const toggleSkipped = trpc.tasks.toggleSkipped.useMutation({
    onSuccess: () => {
      utils.tasks.listByList.invalidate();
      utils.lists.overview.invalidate();
      utils.day.monthSummary.invalidate();
    },
  });
  const addToToday = trpc.tasks.update.useMutation({
    onSuccess: () => {
      utils.tasks.listByList.invalidate();
      utils.lists.overview.invalidate();
      utils.day.list.invalidate({ date: todayKey() });
      utils.day.monthSummary.invalidate();
    },
  });
  const duplicateTask = trpc.tasks.duplicate.useMutation({
    onSuccess: (_data, vars) => {
      utils.tasks.listByList.invalidate();
      utils.lists.overview.invalidate();
      if (vars.date) utils.day.list.invalidate({ date: vars.date });
      utils.day.monthSummary.invalidate();
    },
  });
  const addPatternToToday = trpc.day.addListToDay.useMutation({
    onSuccess: () => {
      utils.tasks.listByList.invalidate();
      utils.lists.overview.invalidate();
      utils.day.list.invalidate({ date: todayKey() });
      utils.day.monthSummary.invalidate();
    },
  });

  function addPattern(section: PatternAddSectionValue) {
    if (!list) return Promise.reject(new Error("List is not loaded"));
    return addPatternToToday.mutateAsync({
      date: todayKey(),
      listId: list.id,
      fallbackStartTime: nowTimeString(),
      selections: [patternSectionToSelection({ ...section, selected: true })],
    }).then(result => {
      setPatternSheet(null);
      return result;
    });
  }

  return (
    <AppShell backTo="/lists" title={list?.name ?? "List"}>
      <div className="container max-w-3xl py-6 space-y-6">
        {listsQuery.isLoading ? (
          <div className="space-y-4">
            <div className="h-20 rounded-xl bg-muted/40 animate-pulse" />
            <div className="h-40 rounded-xl bg-muted/40 animate-pulse" />
          </div>
        ) : !list ? (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center">
            <div className="font-display text-xl">List not found</div>
            <Button
              variant="outline"
              className="mt-4 rounded-full"
              onClick={() => setLocation("/lists")}
            >
              Back to lists
            </Button>
          </div>
        ) : (
          <>
            <header className="flex items-end justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  List
                </p>
                <h1 className="font-display text-3xl sm:text-4xl tracking-tight flex items-center gap-2 min-w-0">
                  <span
                    className="h-3 w-3 rounded-full shrink-0"
                    style={{ background: colors.base }}
                    aria-hidden
                  />
                  <span className="truncate">{list.name}</span>
                </h1>
                {list.description && (
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {list.description}
                  </p>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="gap-2 rounded-full"
                onClick={() => setLocation(`/settings/lists/${list.id}`)}
              >
                <Settings className="h-4 w-4" />
                Edit
              </Button>
            </header>

            <section className="space-y-2">
              <div className="flex items-center justify-between gap-3 px-2">
                <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  Upcoming tasks
                </h2>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {tasks.length}
                </span>
              </div>
              {tasksQuery.isLoading ? (
                <div className="space-y-1.5">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="h-10 rounded-lg bg-muted/40 animate-pulse" />
                  ))}
                </div>
              ) : tasks.length === 0 ? (
                <div className="rounded-xl border border-border/60 bg-card px-3 py-3 text-sm text-muted-foreground">
                  Nothing upcoming.
                </div>
              ) : (
                <ul className="task-list divide-y divide-border/50 border border-border/60 rounded-xl overflow-hidden bg-card">
                  {tasks.map((t, index) => {
                    const lifecycle = deriveTaskLifecycle(t);
                    return <li key={t.id} className="task">
                      {shouldShowStatusDivider(tasks, index) && (
                        <TaskStatusDivider
                          status={lifecycle}
                          count={statusCount(tasks, lifecycle)}
                          collapsed={statusGroups.collapsed.has(lifecycle)}
                          onToggle={() => statusGroups.toggle(lifecycle)}
                        />
                      )}
                      {!statusGroups.collapsed.has(lifecycle) && (
                        <TaskSwipeActions
                          task={t}
                          onSwipeRight={() =>
                            markDone.mutate({
                              id: t.id,
                              clientTimestamp: new Date().toISOString(),
                            })
                          }
                          onSwipeLeft={() =>
                            toggleSkipped.mutate({
                              id: t.id,
                              clientTimestamp: new Date().toISOString(),
                            })
                          }
                        >
                          <ListTaskRow
                            task={t}
                            onOpen={() => setLocation(`/task/${t.id}`)}
                            onAddToToday={() =>
                              addToToday.mutate({
                                id: t.id,
                                date: todayKey(),
                                startTime: null,
                                clientTimestamp: new Date().toISOString(),
                              })
                            }
                            onDuplicate={() =>
                              duplicateTask.mutate({
                                id: t.id,
                                date: t.date ?? null,
                              })
                            }
                          />
                        </TaskSwipeActions>
                      )}
                    </li>;
                  })}
                </ul>
              )}
            </section>

            <section className="space-y-2">
              <div className="flex items-center justify-between gap-3 px-2">
                <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  Patterns
                </h2>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {templates.length}
                </span>
              </div>
              {templatesQuery.isLoading ? (
                <div className="space-y-1.5">
                  {[0, 1].map(i => (
                    <div key={i} className="h-10 rounded-lg bg-muted/40 animate-pulse" />
                  ))}
                </div>
              ) : templates.length === 0 ? (
                <div className="rounded-xl border border-border/60 bg-card px-3 py-3 text-sm text-muted-foreground">
                  No patterns yet.
                </div>
              ) : (
                <div className="divide-y divide-border/50 border border-border/60 rounded-xl overflow-hidden bg-card">
                  {templates.map(t => (
                    <ListTemplateRow
                      key={t.id}
                      template={t}
                      variant="detailed"
                      listColor={list.color}
                      listBgColor={list.bgColor ?? null}
                      onOpen={() =>
                        setPatternSheet(
                          createPatternAddSection(t, nowTimeString(), {
                            selected: true,
                            expanded: true,
                          }),
                        )
                      }
                      onAddToToday={() =>
                        addPattern(
                          createPatternAddSection(t, nowTimeString(), {
                            selected: true,
                            expanded: false,
                          }),
                        )
                      }
                    />
                  ))}
                </div>
              )}
            </section>

            <Button
              onClick={() => setCreateSheetOpen(true)}
              size="lg"
              className="fixed bottom-6 right-6 z-30 h-14 w-14 rounded-full shadow-lg gap-0 p-0"
              aria-label={`Add task to ${list.name}`}
            >
              <Plus className="h-5 w-5" />
            </Button>

            <CreateTaskSheet
              open={createSheetOpen}
              onOpenChange={setCreateSheetOpen}
              listId={list.id}
            />

            <Dialog
              open={!!patternSheet}
              onOpenChange={open => {
                if (!open) setPatternSheet(null);
              }}
            >
              <DialogContent className="max-w-2xl max-h-[88dvh] overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle>Add pattern</DialogTitle>
                </DialogHeader>
                {patternSheet && (
                  <div className="flex min-h-0 flex-col gap-4">
                    <div className="min-h-0 overflow-y-auto pr-1">
                      <PatternAddSection
                        value={patternSheet}
                        onChange={setPatternSheet}
                        listName={list.name}
                        listColor={list.color}
                        listBgColor={list.bgColor ?? null}
                      />
                    </div>
                    <div className="flex shrink-0 justify-end gap-2 border-t border-border/60 pt-3">
                      <Button
                        variant="outline"
                        onClick={() => setPatternSheet(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        disabled={addPatternToToday.isPending}
                        onClick={() => addPattern(patternSheet)}
                      >
                        Add to today
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </AppShell>
  );
}
